import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User, LoginCredentials, SignupCredentials, ApiResponse } from '../../types/portal';
import { apiService } from '../../services/api';
import { mapBackendRoleToFrontend } from '../../utils/roleMapper';

// LocalStorage key used to remember if a user has an active session (set after successful login)
const AUTH_PERSISTENCE_KEY = 'ud_isAuthenticated';

// API functions for authentication
const authenticateUser = async (credentials: LoginCredentials): Promise<ApiResponse<User>> => {
    try {
        const userResponse = await apiService.post<User>('/api/auth/login', credentials);
        return {
            success: true,
            data: userResponse,
            message: 'Login successful'
        };
    } catch (error) {
        let finalMessage = 'Unable to sign in. Please try again.';
        let status: number | undefined = undefined;
        if (error && typeof error === 'object') {
            const e: any = error;
            status = e.status as number | undefined;
            const msg = e instanceof Error ? e.message : undefined;
            const msgLower = (msg || '').toLowerCase();
            const containsBadCredentials = msgLower.includes('bad credentials');
            if (status === 401 || containsBadCredentials) {
                finalMessage = 'Invalid username or password';
            } else if (status === 403) {
                finalMessage = 'Your account is disabled. Please contact support.';
            } else if (status === 423) {
                finalMessage = 'Your account is locked due to too many failed attempts. Please try again later or reset your password.';
            } else if (status === 429) {
                finalMessage = 'Too many attempts. Please wait a moment and try again.';
            } else if (status === 400) {
                finalMessage = msg || 'Please check your credentials and try again.';
            } else {
                finalMessage = msg || finalMessage;
            }
        }
        return {
            success: false,
            error: finalMessage
        };
    }
};

const getCurrentUser = async (): Promise<ApiResponse<User>> => {
  try {
    const user = await apiService.get<User>('/api/auth/me');
    return {
      success: true,
      data: user,
      message: 'User retrieved successfully'
    };
  } catch (error) {
    return {
      success: false,
      error: 'Failed to get current user'
      // error: error instanceof Error ? error.message : 'Failed to get current user'
    };
  }
};

const logoutFromServer = async (): Promise<ApiResponse<void>> => {
  try {
    await apiService.post('/api/auth/logout');
    return {
      success: true,
      message: 'Logout successful'
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Logout failed'
    };
  }
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authenticateUser(credentials);
      if (response.success && response.data) {
        // Persist a simple flag indicating an authenticated session exists
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem(AUTH_PERSISTENCE_KEY, 'true');
          }
        } catch (_) { /* ignore storage errors */ }
        // Map the role from the role array
        const backendRole = response.data.roles?.[0]?.name || '';
        const mappedRole = mapBackendRoleToFrontend(backendRole);
        return { ...response.data, role: mappedRole };
      } else {
        // Ensure flag is cleared on failed login
        try {
          if (typeof window !== 'undefined') {
            localStorage.removeItem(AUTH_PERSISTENCE_KEY);
          }
        } catch (_) { /* ignore storage errors */ }
        // Prefer server-provided message when available (apiService extracts it), fallback to safe generic
        return rejectWithValue(response.error || 'Invalid username or password');
      }
    } catch (error) {
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem(AUTH_PERSISTENCE_KEY);
        }
      } catch (_) { /* ignore storage errors */ }
      // Keep a generic network error that does not reveal credential validity
      return rejectWithValue('Unable to sign in. Please try again.');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (credentials: SignupCredentials, { rejectWithValue }) => {
    try {
      const response = await apiService.post<any>('/api/auth/signup', credentials);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Registration failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      // Logout from the server first
      await logoutFromServer();
      // Clear the persisted auth flag
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem(AUTH_PERSISTENCE_KEY);
        }
      } catch (_) { /* ignore storage errors */ }
      return true;
    } catch (error) {
      return rejectWithValue('Logout failed');
    }
  }
);

export const checkAuthStatus = createAsyncThunk(
  'auth/checkAuthStatus',
  async (_, { rejectWithValue }) => {
    try {
      // Do not call the server unless we have evidence of a previous authenticated session
      try {
        if (typeof window !== 'undefined') {
          const hasSession = localStorage.getItem(AUTH_PERSISTENCE_KEY) === 'true';
          if (!hasSession) {
            return null;
          }
        }
      } catch (_) { /* ignore storage errors and proceed */ }

      const response = await getCurrentUser();
      if (response.success && response.data) {
        const backendRole = response.data.roles?.[0]?.name || '';
        const mappedRole = mapBackendRoleToFrontend(backendRole);
        return { ...response.data, role: mappedRole };
      }
      return null;
    } catch (error) {
      return rejectWithValue('Failed to check auth status');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await apiService.put<User>('/api/auth/me', profileData);
      // Map the role from backend roles array
      const backendRole = response.roles?.[0]?.name || '';
      const mappedRole = mapBackendRoleToFrontend(backendRole);
      return { ...response, role: mappedRole };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update profile');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (
    payload: { currentPassword?: string; newPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiService.put<User>('/api/auth/password', payload);
      const backendRole = response.roles?.[0]?.name || '';
      const mappedRole = mapBackendRoleToFrontend(backendRole);
      return { ...response, role: mappedRole };
    } catch (error) {
      const e: any = error;
      return rejectWithValue(e?.message || 'Failed to change password');
    }
  }
);

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  logoutSuccess: false,
};

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // setUser: (state, action: PayloadAction<User>) => {
    //   state.user = action.payload;
    // },
    logout: (state) => {
      state.user = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearLogoutSuccess: (state) => {
      state.logoutSuccess = false;
    },
    // updateUser: (state, action: PayloadAction<User>) => {
    //   state.user = action.payload;
    // },
  },
  extraReducers: (builder) => {
    // Login cases
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        const payloadMsg = (action.payload as string) || '';
        state.error = payloadMsg || action.error?.message || 'Unable to sign in. Please try again.';
      });

    // Logout cases
    builder
      .addCase(logoutUser.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.error = null;
        state.logoutSuccess = true;
      });

    // Check auth status cases
    builder
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload;
          // Keep the persistence flag on successful auth check
          try {
            if (typeof window !== 'undefined') {
              localStorage.setItem(AUTH_PERSISTENCE_KEY, 'true');
            }
          } catch (_) { /* ignore storage errors */ }
        } else {
          state.isAuthenticated = false;
          state.user = null;
          // Clear the flag if no user is returned
          try {
            if (typeof window !== 'undefined') {
              localStorage.removeItem(AUTH_PERSISTENCE_KEY);
            }
          } catch (_) { /* ignore storage errors */ }
        }
      })
      .addCase(checkAuthStatus.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        // Clear the flag when auth check fails (e.g., expired session)
        try {
          if (typeof window !== 'undefined') {
            localStorage.removeItem(AUTH_PERSISTENCE_KEY);
          }
        } catch (_) { /* ignore storage errors */ }
      });

    // Update profile cases
    builder
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Change password cases
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload; // includes requirePasswordChange=false from backend
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const requestPasswordReset = createAsyncThunk(
  'auth/requestPasswordReset',
  async (
    payload: { username: string; email: string },
    { rejectWithValue }
  ) => {
    try {
      const resp = await apiService.post<any>('/api/auth/password/forgot', payload);
      const msg = (resp && (resp.message || resp.msg)) || 'If an account exists for that username and email, we’ve sent password reset instructions.';
      return { message: msg };
    } catch (error: any) {
      const e: any = error;
      const status: number | undefined = e?.status;
      if (status === 429) {
        return rejectWithValue('Too many password reset requests. Please try again later.');
      }
      if (status === 423) {
        return rejectWithValue('Your account is temporarily blocked due to too many reset requests. Please contact your teacher or an admin to unlock it.');
      }
      return rejectWithValue(e?.message || 'An issue has occurred, try again later');
    }
  }
);

export const { clearError, clearLogoutSuccess } = authSlice.actions;
export default authSlice.reducer;